import styles from './OptionWithEditIcon.module.css'

import { RxPencil2 } from '@react-icons/all-files/rx/RxPencil2'
import type { ReactNode } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Flex, IconButton, Text } from 'components/ds'

export interface OptionWithEditIconProps {
  /** Display label for the option */
  label: string
  /** Optional secondary text to display below the label */
  secondaryText?: string
  /** Optional URL to navigate to when edit icon is clicked */
  editUrl?: string
  /** Optional callback when edit icon is clicked */
  onEditClick?: (e: React.MouseEvent) => void
  /** Optional additional content to display */
  children?: ReactNode
}

/**
 * OptionWithEditIcon - A dropdown option component that displays a label with an edit icon
 *
 * This component is designed to be used with SearchableDropdown's renderOption prop.
 * It displays the option label, optional secondary text,
 * and an edit icon that can be clicked to navigate to an edit route or trigger a custom action.
 *
 * @param props - Component props
 * @returns OptionWithEditIcon component
 *
 * @example
 * ```tsx
 * <SearchableDropdown
 *   renderOption={(option) => (
 *     <OptionWithEditIcon
 *       label={option.label}
 *       secondaryText="Helper text"
 *       editUrl={`/contacts/writers/${option.id}/edit`}
 *     />
 *   )}
 * />
 * ```
 */
export function OptionWithEditIcon({
  label,
  secondaryText,
  editUrl,
  onEditClick,
  children,
}: OptionWithEditIconProps) {
  const navigate = useNavigate()

  const handleMouseDown = (e: React.MouseEvent) => {
    // Stop propagation on mousedown to prevent SearchableDropdown's handler
    e.stopPropagation()
    e.preventDefault()

    // Execute the action on mousedown since parent prevents click from firing
    if (onEditClick) {
      onEditClick(e)
    } else if (editUrl) {
      navigate(editUrl)
    }
  }

  const handleEditClick = (e: React.MouseEvent) => {
    // This handler is kept as fallback but may not fire due to parent's preventDefault
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <Flex align="center" justify="between" gap="2" className={styles.container}>
      <Box className={styles.label}>
        <Text size="2">{label}</Text>
        {secondaryText && (
          <Text size="1" color="gray" className={styles.secondaryText}>
            {secondaryText}
          </Text>
        )}
        {children}
      </Box>
      <IconButton
        size="1"
        variant="ghost"
        color="gray"
        onClick={handleEditClick}
        onMouseDown={handleMouseDown}
        className={styles.editButton}
      >
        <RxPencil2 size={16} />
      </IconButton>
    </Flex>
  )
}

export default OptionWithEditIcon
